import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';




const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut, openLoginModal } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userRole, setUserRole] = useState('artist');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const profileRef = useRef(null);
  const notificationRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (userProfile?.role) {
      setUserRole(userProfile?.role);
    }
  }, [userProfile]);

  // Fetch notifications
  useEffect(() => {
    if (!user?.id) return;

    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(5);

        if (error) {
          console.error('Error fetching notifications:', error?.message);
          return;
        }

        setNotifications(data || []);
        setUnreadCount(data?.filter((n) => !n?.read)?.length || 0);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();

    // Real-time subscription
    const channel = supabase?.channel('header_notifications')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setNotifications((prev) => [payload?.new, ...prev?.slice(0, 4)]);
            setUnreadCount((prev) => prev + 1);
          } else if (payload?.eventType === 'UPDATE') {
            setNotifications((prev) =>
              prev?.map((n) => (n?.id === payload?.new?.id ? payload?.new : n))
            );
            setUnreadCount(
              notifications?.filter((n) => !n?.read)?.length || 0
            );
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef?.current && !profileRef?.current?.contains(event?.target)) {
        setIsProfileOpen(false);
      }
      if (notificationRef?.current && !notificationRef?.current?.contains(event?.target)) {
        setIsNotificationOpen(false);
      }
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Public navigation items (visible to everyone)
  const publicNavItems = [
    {
      label: 'Keşfet',
      path: '/',
      icon: 'Search',
      tooltip: 'Influencer keşfet'
    },
    {
      label: 'Influencerlar',
      path: '/influencers',
      icon: 'Users',
      tooltip: 'Tüm influencerlar'
    },
    {
      label: 'Kampanyalar',
      path: '/campaigns',
      icon: 'Briefcase',
      tooltip: 'Kampanyalar'
    },
  ];

  // Private navigation items (only for logged-in users)
  const privateNavItems = [
    {
      label: 'Dashboard',
      path: userRole === 'artist' ? '/artist-dashboard' : '/influencer-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'View your dashboard',
      requiresAuth: true
    },
    {
      label: 'Mesajlar',
      path: '/messages',
      icon: 'MessageSquare',
      tooltip: 'Direct messages',
      requiresAuth: true
    },
    {
      label: 'Analytics',
      path: '/analytics-dashboard',
      icon: 'BarChart3',
      tooltip: 'View analytics',
      requiresAuth: true
    },
    {
      label: 'Music Links',
      path: '/music-links',
      icon: 'Link',
      tooltip: 'Smart music links',
      requiresAuth: true
    },
    {
      label: 'Click Analytics',
      path: '/click-analytics',
      icon: 'MousePointerClick',
      tooltip: 'Influencer click analytics',
      requiresAuth: true
    },
  ];

  // All users see public nav; logged-in users also see private nav
  const navigationItems = user ? [...publicNavItems, ...privateNavItems] : publicNavItems;

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = (path, requiresAuth = false) => {
    if (requiresAuth && !user) {
      openLoginModal();
      return;
    }
    navigate(path);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.read) {
      try {
        await supabase?.from('notifications')?.update({ read: true })?.eq('id', notification?.id)?.eq('user_id', user?.id);

        setNotifications((prev) =>
          prev?.map((n) => (n?.id === notification?.id ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    if (notification?.action_url) {
      navigate(notification?.action_url);
      setIsNotificationOpen(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'campaign':
        return 'Briefcase';
      case 'message':
        return 'MessageSquare';
      case 'payment':
        return 'DollarSign';
      case 'proposal':
        return 'FileText';
      default:
        return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString();
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleRoleSwitch = (role) => {
    setUserRole(role);
    const dashboardPath = role === 'artist' ? '/artist-dashboard' : '/influencer-dashboard';
    navigate(dashboardPath);
    setIsProfileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const profileMenuItems = [
    {
      label: userRole === 'artist' ? 'Artist Profile' : 'Influencer Profile',
      icon: 'User',
      onClick: () => handleNavigation(userRole === 'artist' ? '/artist-profile-creation' : '/influencer-profile-creation')
    },
    {
      label: 'Profile Setup',
      icon: 'CheckSquare',
      onClick: () => handleNavigation('/profile-setup-checklist')
    },
    {
      label: 'Settings',
      icon: 'Settings',
      onClick: () => console.log('Settings clicked')
    },
    {
      label: 'Help & Support',
      icon: 'HelpCircle',
      onClick: () => handleNavigation('/help')
    },
    {
      label: 'Sign Out',
      icon: 'LogOut',
      onClick: handleLogout
    }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-[1000]">
      <div className="h-full px-6 flex items-center justify-between">
        <div className="flex items-center" style={{ gap: '24px' }}>
          <img
            src="/assets/images/Untitled_Project-1775216403728.png"
            alt="OzyMakers"
            style={{ height: '40px', width: 'auto', display: 'block', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => handleNavigation('/')}
          />

          <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path, item?.requiresAuth)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                  transition-smooth hover:bg-muted
                  ${isActive(item?.path) 
                    ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                  }
                `}
                aria-current={isActive(item?.path) ? 'page' : undefined}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div ref={searchRef} className="relative">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Influencer ara..."
                  className="w-64 px-4 py-2 pr-10 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }}
                  className="absolute right-2 p-1 hover:bg-background rounded transition-smooth"
                  aria-label="Close search"
                >
                  <Icon name="X" size={16} />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 hover:bg-muted rounded-md transition-smooth"
                aria-label="Open search"
                title="Search"
              >
                <Icon name="Search" size={20} />
              </button>
            )}
          </div>

          {/* Show different right-side controls based on auth state */}
          {user ? (
            <>
              {/* Notifications — only for logged-in users */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  className="relative p-2 rounded-lg hover:bg-muted transition-colors"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} className="text-muted-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full"></span>
                  )}
                </button>

                {isNotificationOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">Bildirimler</h3>
                      {unreadCount > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {unreadCount} okunmamış
                        </span>
                      )}
                    </div>
                    <div className="divide-y divide-border">
                      {notifications?.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground text-sm">
                          Bildirim yok
                        </div>
                      ) : (
                        notifications?.map((notification) => (
                          <button
                            key={notification?.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                              !notification?.read ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`p-2 rounded-lg ${
                                  notification?.category === 'campaign' ? 'bg-blue-500/10 text-blue-600'
                                    : notification?.category === 'payment' ? 'bg-orange-500/10 text-orange-600'
                                    : notification?.category === 'message' ? 'bg-green-500/10 text-green-600' : 'bg-purple-500/10 text-purple-600'
                                }`}
                              >
                                <Icon name={getCategoryIcon(notification?.category)} size={16} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-foreground">
                                    {notification?.title}
                                  </p>
                                  {!notification?.read && (
                                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1"></span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {notification?.message}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatTimestamp(notification?.created_at)}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    {notifications?.length > 0 && (
                      <div className="p-3 border-t border-border">
                        <button
                          onClick={() => {
                            navigate('/notification-center');
                            setIsNotificationOpen(false);
                          }}
                          className="w-full text-center text-sm text-primary hover:text-primary/80 font-medium"
                        >
                          Tüm bildirimleri gör
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile menu — only for logged-in users */}
              <div ref={profileRef} className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 p-2 hover:bg-muted rounded-md transition-smooth"
                  aria-label="User menu"
                  aria-expanded={isProfileOpen}
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={18} color="var(--color-primary)" />
                  </div>
                  <Icon name="ChevronDown" size={16} className="hidden sm:block" />
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-elevation-3 animate-slide-in z-[1100]">
                    <div className="p-4 border-b border-border">
                      <p className="font-semibold text-sm">{userProfile?.full_name || 'Kullanıcı'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{userRole}</p>
                    </div>
                    
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                        Rol Değiştir
                      </div>
                      <button
                        onClick={() => handleRoleSwitch('artist')}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-smooth
                          ${userRole === 'artist' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
                        `}
                      >
                        <Icon name="Music" size={16} />
                        <span>Artist Dashboard</span>
                      </button>
                      <button
                        onClick={() => handleRoleSwitch('influencer')}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-smooth
                          ${userRole === 'influencer' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
                        `}
                      >
                        <Icon name="Users" size={16} />
                        <span>Influencer Dashboard</span>
                      </button>
                    </div>

                    <div className="p-2 border-t border-border">
                      {profileMenuItems?.map((item) => (
                        <button
                          key={item?.label}
                          onClick={item?.onClick}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-muted transition-smooth"
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Public CTA buttons for unauthenticated visitors */
            <div className="flex items-center gap-2">
              <button
                onClick={openLoginModal}
                className="flex items-center gap-2 px-4 py-2 border border-primary text-primary hover:bg-primary/10 rounded-md text-sm font-medium transition-smooth"
              >
                <Icon name="LogIn" size={16} />
                <span>Giriş</span>
              </button>
              <button
                onClick={openLoginModal}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-medium transition-smooth"
              >
                <Icon name="Plus" size={16} />
                <span>Kampanya Oluştur</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;